import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiRequest } from "../api";
import { fetchAccess } from "../access";
import { buildDocumentTimestamp } from "../utils/documentTimestamp";

import BusinessHeader from "../documents/layouts/BusinessHeader";
import ClientBlock from "../documents/layouts/ClientBlock";

import "../documents/styles/print.css";

function money(n) {
  if (n === null || n === undefined) return "$0.00";
  const v = typeof n === "string" ? Number(n) : n;
  return (isNaN(v) ? 0 : v).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d || "—");
  }
}

function ReceiptPageFooter({ businessName, receiptNumber }) {
  return (
    <div className="jt-receipt-page-footer">
      <div className="jt-receipt-page-footer-row">
        <span>{businessName}</span>
        <span>Receipt {receiptNumber} - Page 1 of 1</span>
      </div>
      <div className="jt-receipt-product-line">
        Prepared by <b>{businessName}</b> using <b>JobTelesis</b>
      </div>
    </div>
  );
}

export default function InvoiceReceipt() {
  const { invoiceId, paymentId } = useParams();

  const [inv, setInv] = useState(null);
  const [pay, setPay] = useState(null);
  const [client, setClient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState("");

  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailAction, setEmailAction] = useState(null);
  const [access, setAccess] = useState({ can_send_email: true, can_move_money: true });

  async function load() {
    setErr("");
    try {
      const [invData, payData] = await Promise.all([
        apiRequest(`/invoices/${invoiceId}`),
        apiRequest(`/payments/${paymentId}`),
      ]);

      setInv(invData);
      setPay(payData);

      if (invData?.client_id) {
        try {
          setClient(await apiRequest(`/clients/${invData.client_id}`));
        } catch {
          setClient(null);
        }
      }

      try {
        setProfile(await apiRequest(`/profile`));
      } catch {
        setProfile(null);
      }
    } catch (e) {
      setErr(e.message || "Failed to load receipt.");
    }
  }

  useEffect(() => {
    fetchAccess().then(setAccess);
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, paymentId]);

  async function saveCommunicationNote() {
    if (!inv?.job_id) {
      setEmailError("This receipt is not linked to a project/job yet.");
      return;
    }

    try {
      const now = new Date().toLocaleString();
      const me = await apiRequest("/auth/me");
      const senderEmail = me?.email || "unknown user";
      const receiptNo = `RCPT-${String(pay.id).padStart(6, "0")}`;

      const note = `[CLIENT COMMUNICATION]

Document Type: Receipt
Document Number: ${receiptNo}

PDF File:
${emailAction.fileName}

Recipient:
${client?.email || ""}

Date/Time:
${now}

Sent By:
${senderEmail}

Subject:
${emailAction.subject}

Delivery Method:
Tenant Email Client

Status:
Marked sent by user`;

      await apiRequest(`/notes/job/${inv.job_id}`, {
        method: "POST",
        body: { content: note },
      });

      await apiRequest(`/document-activity`, {
        method: "POST",
        body: {
          job_id: inv.job_id,
          client_id: inv.client_id || null,
          document_type: "RECEIPT",
          document_id: Number(pay.id),
          document_number: receiptNo,
          activity_type: "EMAILED",
          amount: pay.amount || null,
          email_to: client?.email || "",
          subject: emailAction.subject,
          file_name: emailAction.fileName,
          notes: "Receipt emailed through tenant email client",
        },
      });

      setEmailSuccess("Communication note + ledger activity saved.");
      setEmailAction(null);
    } catch (e) {
      console.error(e);
      setEmailError(e?.message || "Failed to save communication note.");
    }
  }

  async function sendReceiptEmail() {
    const gate = await fetchAccess();
    if (!gate.can_send_email) return;

    if (!inv || !pay) return;

    const clientEmail = client?.email || "";
    const clientName =
      client?.company_name || client?.contact_name || client?.name || "Client";

    if (!clientEmail) {
      setEmailError("Client email is missing. Add a client email before preparing this receipt email.");
      setEmailSuccess("");
      return;
    }

    setEmailSending(true);
    setEmailSuccess("");
    setEmailError("");
    setEmailAction(null);

    try {
      const receiptNo = `RCPT-${String(pay.id).padStart(6, "0")}`;
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "-")
        .slice(0, 15);

      const jobPart = inv?.job_id ? `-JOB${inv.job_id}` : "";
      const pdfFileName = `Receipt-${receiptNo}${jobPart}-${timestamp}.pdf`;

      window.open(window.location.href, "_blank", "noopener,noreferrer");

      const subject = `Payment Receipt ${receiptNo}`;
      const body = `Hi ${clientName},

I hope you’re doing well.

Please see the attached payment receipt PDF for your records.

Thank you,

`;

      const mailto =
        `mailto:${encodeURIComponent(clientEmail)}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      window.location.href = mailto;

      try {
        await fetch("/api/document-activity", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "x-tenant": localStorage.getItem("x-tenant") || "dfasio",
          },
          body: JSON.stringify({
            job_id: inv?.job_id || null,
            client_id: inv?.client_id || null,
            document_type: "receipt",
            document_id: pay?.id || null,
            document_number: receiptNo,
            activity_type: "EMAIL_PREPARED",
            email_to: clientEmail,
            subject,
            file_name: pdfFileName,
            notes: "Receipt Prepare Email triggered from Receipt page.",
          }),
        });
      } catch (telemetryError) {
        console.error("OET telemetry error:", telemetryError);
      }

      setEmailAction({
        fileName: pdfFileName,
        clientEmail,
        subject,
        jobId: inv?.job_id,
        documentType: "Receipt",
        documentNumber: receiptNo,
      });

      setEmailSuccess(
        "Receipt print page opened. Save it as a PDF, then attach it to the email draft before sending."
      );
    } catch (e) {
      console.error("Receipt email error:", e);
      setEmailError(String(e?.message || e || "Failed to prepare receipt email."));
    } finally {
      setEmailSending(false);
    }
  }

  if (err) {
    return (
      <div style={{ padding: 24, color: "#fff", background: "#061a2f" }}>
        <p style={{ color: "#ff8b8b" }}>{err}</p>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  if (!inv || !pay) {
    return (
      <div style={{ padding: 24, color: "#fff", background: "#061a2f" }}>
        Loading…
      </div>
    );
  }

  const companyName =
    profile?.display_name?.trim() ||
    profile?.legal_name?.trim() ||
    profile?.company_name ||
    "Your Company";

  const companyAddress = [
    profile?.address_line1,
    profile?.address_line2 ? `Suite ${profile.address_line2}` : null,
    [profile?.city, profile?.state, profile?.postal_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join("\n");

  const clientName =
    client?.company_name || client?.contact_name || client?.name || "Client";

  const clientAddress = [
    client?.address,
    client?.address2,
    [client?.city, client?.state, client?.zip || client?.postal_code]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join("\n");

  const invoiceTotal = Number(inv.total || 0);
  const paidAfterThis = Number(inv.amount_paid || 0);
  const amountReceived = Number(pay.amount || 0);
  const balance = Math.max(invoiceTotal - paidAfterThis, 0);
  const receiptStatus = balance === 0 ? "PAID" : "PARTIAL";
  const receiptNo = `RCPT-${String(pay.id).padStart(6, "0")}`;
  const receiptJobPartForTitle = inv?.job_id ? `-JOB${inv.job_id}` : "";
  document.title = `Receipt-${receiptNo}${receiptJobPartForTitle}-${buildDocumentTimestamp()}`;

  return (
    <div className="jt-print-page">
      <div className="jt-print-sheet jt-receipt-print-sheet">
        <div className="no-print" style={{ marginBottom: 22, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <Link to={`/invoices/${inv.id}`} style={{ color: "#00c2ff", fontWeight: 800, textDecoration: "none" }}>
            ← Back to Invoice
          </Link>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {access.can_send_email && (
            <button
              onClick={sendReceiptEmail}
              disabled={emailSending}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: 0,
                background: "#f5c542",
                color: "#062033",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              {emailSending ? "Preparing…" : "Prepare Email"}
            </button>
            )}

            <button
              onClick={() => window.print()}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: 0,
                background: "#00c2ff",
                color: "#062033",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Print / Save PDF
            </button>
          </div>
        </div>

        {emailAction && (
          <div className="no-print" style={{ marginBottom: 16, padding: 16, borderRadius: 12, background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.35)", color: "#5b4500" }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>
              Take Action: Save and Attach the Receipt PDF
            </div>
            <div style={{ fontSize: 14 }}>
              Save this receipt page as a PDF, then attach this file name to your email draft:
            </div>
            <div style={{ marginTop: 8, padding: "8px 10px", background: "#ffffff", borderRadius: 8, fontFamily: "monospace", fontSize: 13 }}>
              {emailAction.fileName}
            </div>
            <button
              type="button"
              onClick={saveCommunicationNote}
              style={{
                marginTop: 10,
                padding: "8px 12px",
                background: "#166534",
                color: "#ffffff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 800,
              }}
            >
              Mark Sent & Save Job Note
            </button>
          </div>
        )}

        {(emailSuccess || emailError) && (
          <div className="no-print" style={{ marginBottom: 12, fontSize: 13 }}>
            {emailSuccess && <div style={{ color: "green" }}>{emailSuccess}</div>}
            {emailError && <div style={{ color: "crimson" }}>{emailError}</div>}
          </div>
        )}

        <BusinessHeader
          businessName={companyName}
          businessAddress={companyAddress}
          phone={profile?.phone}
          email={profile?.email}
          website={profile?.website}
          documentTitle="Receipt"
          documentNumber={receiptNo}
          issueDate={fmtDate(pay.received_at || pay.paid_date || pay.created_at)}
          status={receiptStatus}
        />

        <ClientBlock
          title="Received From"
          clientName={clientName}
          address={clientAddress}
          phone={client?.phone}
          email={client?.email}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 280px",
            gap: 16,
            marginTop: 14,
          }}
        >
          <div
            style={{
              border: "1px solid #dbe7f3",
              borderRadius: 14,
              padding: 16,
              background: "#fbfdff",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#8a6a00",
                letterSpacing: 0.7,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Payment Details
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "8px 14px", color: "#5c6b78" }}>
              <div>Applied To</div>
              <strong style={{ color: "#12202f" }}>{inv.invoice_number || `INV-${inv.id}`}</strong>

              <div>Payment Method</div>
              <strong style={{ color: "#12202f" }}>{pay.method || "—"}</strong>

              <div>Reference</div>
              <strong style={{ color: "#12202f" }}>{pay.reference || "—"}</strong>

              <div>Received Date</div>
              <strong style={{ color: "#12202f" }}>
                {fmtDate(pay.received_at || pay.paid_date || pay.created_at)}
              </strong>
            </div>
          </div>

          <div className="jt-document-section jt-doc-totals-box">
            <div className="jt-doc-totals-row">
              <span>Amount Received</span>
              <strong>{money(amountReceived)}</strong>
            </div>

            <div className="jt-doc-totals-row">
              <span>Invoice Total</span>
              <strong>{money(invoiceTotal)}</strong>
            </div>

            <div className="jt-doc-totals-row">
              <span>Paid After This</span>
              <strong>{money(paidAfterThis)}</strong>
            </div>

            <div className="jt-doc-totals-divider" />

            <div className="jt-doc-grand-total">
              <span>Balance</span>
              <strong>{money(balance)}</strong>
            </div>
          </div>
        </div>

        <div className="jt-doc-footer">
          <div className="jt-doc-footer-message">
            <div className="jt-doc-footer-icon">✓</div>
            <div>
              <div className="jt-doc-footer-headline">
                Payment received. Thank you for your business.
              </div>
            </div>
          </div>
        </div>

        <ReceiptPageFooter businessName={companyName} receiptNumber={receiptNo} />
      </div>
    </div>
  );
}
